import React, { useCallback, useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { ChevronLeft } from "@gravity-ui/icons";
import { getVideoSettings, startLive, stopLive } from "../../services/liveArchive";
import "./LivePage.css";

const COLD_START_TIMEOUT_MS = 15000;

const isIOS = () =>
  /iPad|iPhone|iPod/.test(navigator.userAgent) ||
  (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

const getIosFallbackUrl = (desktopUrl = "") =>
  desktopUrl
    .replace("/video/hls/", "/video/hls-ios/")
    .replace("?cookieCheck=1", "")
    .replace("&cookieCheck=1", "");

const getErrorMessage = (error) => {
  const status = error?.response?.status;

  if (status === 401) return "Требуется авторизация";
  if (status === 403) return "Нет доступа к этой камере";
  if (status === 404) return "Камера недоступна";
  if (status === 502) return "Видеосервис временно недоступен";
  // Сырой текст ответа backend пользователю не показывается.
  return "Не удалось запустить эфир";
};

const STATUS_TEXT = {
  idle: "",
  starting: "Подключаемся к камере…",
  buffering: "Ожидаем видеопоток…",
  playing: "Прямой эфир активен",
  stopped: "Эфир остановлен",
};

const LivePage = () => {
  const { cameraId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fallback = location.pathname.startsWith("/equipment") ? "/equipment" : "/cameras";
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const coldStartTimerRef = useRef(null);
  const isStartingRef = useRef(false);
  const startedRef = useRef(false);
  const cancelledRef = useRef(false);

  const [cameraName, setCameraName] = useState(`Камера ${cameraId}`);
  const [liveEnabled, setLiveEnabled] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [notFound, setNotFound] = useState(false);

  const cleanupPlayer = useCallback(() => {
    if (coldStartTimerRef.current) {
      clearTimeout(coldStartTimerRef.current);
      coldStartTimerRef.current = null;
    }
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.removeAttribute("src");
      videoRef.current.removeAttribute("crossorigin");
      videoRef.current.load();
    }
  }, []);

  useEffect(() => {
    cancelledRef.current = false;

    getVideoSettings(cameraId)
      .then((settings) => {
        if (cancelledRef.current) return;
        setCameraName(settings?.camera?.name || `Камера ${cameraId}`);
        setLiveEnabled(Boolean(settings?.live_enabled));
        if (!settings?.live_enabled) {
          setError("Для активации прямого эфира обратитесь к администратору");
        }
      })
      .catch((settingsError) => {
        if (cancelledRef.current) return;
        setLiveEnabled(false);
        setError(getErrorMessage(settingsError));
        if (settingsError?.response?.status === 404) setNotFound(true);
      });

    return () => {
      cancelledRef.current = true;
      cleanupPlayer();
      if (startedRef.current) {
        startedRef.current = false;
        stopLive(cameraId).catch(() => null);
      }
    };
  }, [cameraId, cleanupPlayer]);

  useEffect(() => {
    if (!notFound) return;
    const timer = setTimeout(() => {
      if (location.key === "default") {
        navigate(fallback, { replace: true });
      } else {
        navigate(-1);
      }
    }, 1800);
    return () => clearTimeout(timer);
  }, [notFound, navigate, fallback, location.key]);

  const armColdStartTimeout = useCallback(() => {
    if (coldStartTimerRef.current) clearTimeout(coldStartTimerRef.current);
    coldStartTimerRef.current = setTimeout(() => {
      if (cancelledRef.current) return;
      cleanupPlayer();
      setStatus("error");
      setError("Не удалось запустить эфир");
    }, COLD_START_TIMEOUT_MS);
  }, [cleanupPlayer]);

  const handlePlaying = useCallback(() => {
    if (cancelledRef.current) return;
    if (coldStartTimerRef.current) {
      clearTimeout(coldStartTimerRef.current);
      coldStartTimerRef.current = null;
    }
    setStatus("playing");
  }, []);

  const attachDesktopPlayer = useCallback((url) => {
    const video = videoRef.current;
    if (!video) return;

    video.addEventListener("playing", handlePlaying);

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.removeAttribute("crossorigin");
      video.src = url;
      video.load();
      setStatus("buffering");
      video.play().catch(() => null);
      return;
    }

    if (!Hls.isSupported()) {
      setStatus("error");
      setError("Этот браузер не поддерживает HLS-плеер");
      return;
    }

    const hls = new Hls();
    hlsRef.current = hls;

    hls.on(Hls.Events.MEDIA_ATTACHED, () => {
      hls.loadSource(url);
    });

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      if (cancelledRef.current) return;
      setStatus("buffering");
      video.play().catch(() => null);
    });

    hls.on(Hls.Events.ERROR, (_, data) => {
      if (cancelledRef.current || !data?.fatal) return;

      if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
        hls.startLoad();
        return;
      }
      if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
        hls.recoverMediaError();
        return;
      }

      cleanupPlayer();
      setStatus("error");
      setError("Не удалось запустить эфир");
    });

    hls.attachMedia(video);
  }, [cleanupPlayer, handlePlaying]);

  const attachIOSPlayer = useCallback(async (url) => {
    const video = videoRef.current;
    if (!video) return;

    const nativeHlsSupported =
      video.canPlayType("application/vnd.apple.mpegurl") ||
      video.canPlayType("application/x-mpegURL");

    if (!nativeHlsSupported) {
      setStatus("error");
      setError("Этот браузер не поддерживает native HLS");
      return;
    }

    video.addEventListener("playing", handlePlaying);
    video.removeAttribute("crossorigin");
    video.setAttribute("playsinline", "true");
    video.setAttribute("webkit-playsinline", "true");
    video.controls = true;
    video.preload = "auto";
    video.src = url;
    video.load();
    setStatus("buffering");

    try {
      await video.play();
    } catch (playError) {
      if (cancelledRef.current) return;
      cleanupPlayer();
      setStatus("error");
      setError("Не удалось запустить эфир");
    }
  }, [cleanupPlayer, handlePlaying]);

  const handleStart = useCallback(async () => {
    if (isStartingRef.current) return;

    isStartingRef.current = true;
    cleanupPlayer();
    setError("");
    setStatus("starting");
    armColdStartTimeout();

    try {
      const started = await startLive(cameraId);
      if (cancelledRef.current) return;

      startedRef.current = true;
      if (started?.camera?.name) setCameraName(started.camera.name);

      const desktopUrl = started?.live_url || "";
      const iosUrl = started?.live_url_ios || getIosFallbackUrl(desktopUrl);

      if (isIOS() && iosUrl) {
        await attachIOSPlayer(iosUrl);
      } else if (desktopUrl) {
        attachDesktopPlayer(desktopUrl);
      } else {
        cleanupPlayer();
        setStatus("error");
        setError("Поток недоступен");
      }
    } catch (startError) {
      if (cancelledRef.current) return;
      cleanupPlayer();
      setStatus("error");
      setError(getErrorMessage(startError));
    } finally {
      isStartingRef.current = false;
    }
  }, [cameraId, cleanupPlayer, armColdStartTimeout, attachIOSPlayer, attachDesktopPlayer]);

  const handleStop = useCallback(() => {
    cleanupPlayer();
    setStatus("stopped");
    setError("");
    if (startedRef.current) {
      startedRef.current = false;
      stopLive(cameraId).catch(() => null);
    }
  }, [cameraId, cleanupPlayer]);

  const showStartButton = liveEnabled && !isStartingRef.current &&
    (status === "idle" || status === "stopped" || status === "error");
  const showStopButton = status === "playing" || status === "buffering";
  const statusText = status === "error" ? error : STATUS_TEXT[status];

  return (
    <div className="live-page">
      <div className="live-header">
        <button className="live-back-btn" onClick={() => navigate(-1)}>
          <ChevronLeft className="g-icon-lead" width={16} height={16} />Назад
        </button>
        <h1>Прямой эфир</h1>
      </div>

      <div className="live-meta">
        <strong>{cameraName}</strong>
      </div>

      {statusText && !error && <div className="live-status">{statusText}</div>}
      {error && <div className="live-error">{error}</div>}

      {showStartButton && (
        <button type="button" className="live-play-btn" onClick={handleStart}>
          {status === "error" ? "Повторить запуск" : "Запустить эфир"}
        </button>
      )}

      {showStopButton && (
        <button type="button" className="live-stop-btn" onClick={handleStop}>
          Остановить эфир
        </button>
      )}

      <div className="live-player">
        <video ref={videoRef} controls playsInline preload="auto" />
      </div>
    </div>
  );
};

export default LivePage;
